import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '../components/ui/button'
import Loader from '../components/ui/loader.jsx'
import Validator from '../utils/validator'
import apiService from '../services/api.service'
import { useTenant } from '../context/TenantContext'
import { useClient } from '../context/ClientContext'
import { useLanguage } from '../context/LanguageContext'
import { BackIcon } from '../components/icons'
import { PasswordInput } from '../components/ui/password-input'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import PasswordCriteriaValidator from '../components/PasswordCriteriaValidator'

const ChangePasswordForm = () => {
  const navigate = useNavigate()
  const { tenanturlname } = useParams()
  const { tenant } = useTenant()
  const { client, logout } = useClient()
  const { languageId, t } = useLanguage()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [formData, setFormData] = useState({
    'current-password': '',
    'new-password': '',
    'confirm-password': '',
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)

  // Redirect if user is not authenticated or email is not available
  useEffect(() => {
    if (!client?.email) {
      const tenantUrlName = tenant?.tenanturlname || tenanturlname
      navigate(`/${tenantUrlName}/signin`)
    }
  }, [client, tenant, tenanturlname, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate current password
    if (!formData['current-password']) {
      newErrors['current-password'] = t(
        'changePassword.errors.currentPasswordRequired',
      )
    }

    // Validate new password
    if (!formData['new-password']) {
      newErrors['new-password'] = t('changePassword.errors.passwordRequired')
    } else {
      const passwordValidation = Validator.isValidPassword(
        formData['new-password'],
      )
      if (!passwordValidation.valid) {
        newErrors['new-password'] = t(
          `changePassword.errors.${passwordValidation.errorCode}`,
        )
      } else if (
        formData['current-password'] &&
        formData['new-password'] === formData['current-password']
      ) {
        newErrors['new-password'] = t(
          'changePassword.errors.passwordSameAsCurrent',
        )
      }
    }

    // Validate confirm password
    if (!formData['confirm-password']) {
      newErrors['confirm-password'] = t('changePassword.errors.confirmRequired')
    } else if (formData['new-password'] !== formData['confirm-password']) {
      newErrors['confirm-password'] = t(
        'changePassword.errors.passwordMismatch',
      )
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      return
    }

    // Check if user email is available
    if (!client?.email) {
      setErrors({ submit: t('changePassword.errors.userEmailNotFound') })
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      let recaptchaToken = null
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('change_password')
        } catch (error) {
          console.error('reCAPTCHA execution failed:', error)
        }
      }

      const response = await apiService.post('/api/kc/change-password', {
        currentPassword: formData['current-password'],
        newPassword: formData['new-password'],
        recaptchaToken,
        defaultlanguageid: languageId || 490,
      })

      // Success - show success state first, then logout and redirect
      setIsSuccess(true)

      // Wait 3 seconds so user can read the success message, then logout and redirect
      setTimeout(async () => {
        await logout(tenant?.tenantid)
        const tenantUrlName = tenant?.tenanturlname || tenanturlname
        navigate(`/${tenantUrlName}/signin`)
      }, 3000)
    } catch (error) {
      console.error('Change password error:', error)

      // Handle API errors - apiService.handleError transforms to { message, status, data, isRateLimit }
      if (error.isRateLimit) {
        setErrors({ submit: t('changePassword.errors.rateLimitExceeded') })
      } else if (
        error.message ===
        'Invalid password: must not be equal to any of last 4 passwords.'
      ) {
        setErrors({
          submit: t('changePassword.errors.invalidPasswordHistoryMessage'),
        })
      } else if (error.data?.message) {
        setErrors({ submit: error.data.message })
      } else {
        setErrors({
          submit:
            error.message || t('changePassword.errors.changePasswordFailed'),
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading && (
        <Loader
          fullscreen
          label={t('changePassword.changingButton')}
          size="xl"
        />
      )}

      <div className="w-full px-6 mt-4 md:max-w-[600px] md:mx-auto">
        {/* Change Password - START */}
        {!isSuccess ? (
          <form
            className="form-container"
            onSubmit={handleSubmit}
            noValidate
            aria-label="Forgot Password form"
          >
            <div className="text-center mb-4">
              <div className="w-full flex items-start">
                <button
                  type="button"
                  onClick={() => {
                    const tenantUrlName = tenant?.tenanturlname || tenanturlname
                    navigate(`/${tenantUrlName}/account-settings`)
                  }}
                  className="cursor-pointer text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
                >
                  <BackIcon className="h-5 w-5 text-theme-primary" />
                </button>
              </div>
              <p className="text-base font-bold text-theme-secondary">
                {t('changePassword.title')}
              </p>
              <p className="text-xs text-theme-secondary mt-2">
                {t('changePassword.subtitle')}
              </p>
            </div>

            {/* Display general error */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Current Password */}
              <div className="grid gap-1">
                <label
                  htmlFor="current-password"
                  className="text-sm font-normal text-theme-accent text-left"
                >
                  {t('changePassword.currentPasswordLabel')}
                </label>
                <PasswordInput
                  id="current-password"
                  name="current-password"
                  value={formData['current-password']}
                  onChange={handleChange}
                  required
                  className={
                    errors['current-password']
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }
                  placeholder={t('changePassword.currentPasswordPlaceholder')}
                  autoComplete="current-password"
                  aria-required="true"
                  aria-invalid={errors['current-password'] ? 'true' : 'false'}
                  aria-describedby={
                    errors['current-password']
                      ? 'current-password-error'
                      : undefined
                  }
                />
                {errors['current-password'] && (
                  <p
                    id="current-password-error"
                    className="text-sm text-red-500 mt-1 text-left"
                  >
                    <div className="flex items-start justify-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0 mt-2"></span>
                      <span> {errors['current-password']}</span>
                    </div>
                  </p>
                )}
              </div>

              {/* New Password */}
              <div className="grid gap-1">
                <label
                  htmlFor="new-password"
                  className="text-sm font-normal text-theme-accent text-left"
                >
                  {t('changePassword.newPasswordLabel')}
                </label>
                <PasswordInput
                  id="new-password"
                  name="new-password"
                  value={formData['new-password']}
                  onChange={handleChange}
                  onFocus={() => setIsPasswordFocused(true)}
                  onBlur={() => setIsPasswordFocused(false)}
                  required
                  className={
                    errors['new-password']
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }
                  placeholder={t('changePassword.newPasswordPlaceholder')}
                  autoComplete="new-password"
                  aria-required="true"
                  aria-invalid={errors['new-password'] ? 'true' : 'false'}
                  aria-describedby={
                    errors['new-password'] ? 'new-password-error' : undefined
                  }
                />

                <PasswordCriteriaValidator
                  password={formData['new-password']}
                  isVisible={isPasswordFocused}
                />

                {errors['new-password'] && (
                  <p
                    id="new-password-error"
                    className="text-sm text-red-500 mt-1 text-left"
                  >
                    <div className="flex items-start justify-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0 mt-2"></span>
                      <span> {errors['new-password']}</span>
                    </div>
                  </p>
                )}
              </div>

              {/* Confirm New Password */}
              <div className="grid gap-1">
                <label
                  htmlFor="confirm-password"
                  className="text-sm font-normal text-theme-accent text-left"
                >
                  {t('changePassword.confirmPasswordLabel')}
                </label>
                <PasswordInput
                  id="confirm-password"
                  name="confirm-password"
                  value={formData['confirm-password']}
                  onChange={handleChange}
                  required
                  className={
                    errors['confirm-password']
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }
                  placeholder={t('changePassword.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                  aria-required="true"
                  aria-invalid={errors['confirm-password'] ? 'true' : 'false'}
                  aria-describedby={
                    errors['confirm-password']
                      ? 'confirm-password-error'
                      : undefined
                  }
                />
                {errors['confirm-password'] && (
                  <p
                    id="confirm-password-error"
                    className="text-sm text-red-500 mt-1 text-left"
                  >
                    <div className="flex items-start justify-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0 mt-2"></span>
                      <span> {errors['confirm-password']}</span>
                    </div>
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <Button
                  id="cancel-update-pass-btn"
                  variant="secondary"
                  type="button"
                  size="lg"
                  disabled={isLoading}
                  onClick={() => {
                    const tenantUrlName = tenant?.tenanturlname || tenanturlname
                    navigate(`/${tenantUrlName}/account-settings`)
                  }}
                >
                  {t('changePassword.cancelButton')}
                </Button>

                <Button
                  id="update-pass-btn"
                  variant="primary"
                  type="submit"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('changePassword.changingButton')
                    : t('changePassword.updateButton')}
                </Button>
              </div>
            </div>
          </form>
        ) : (
          /* Success State */
          <div className="text-center mt-4">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-theme-secondary mb-2">
              {t('changePassword.successTitle')}
            </h2>
            <p className="text-sm text-theme-secondary mb-4">
              {t('changePassword.successMessage')}
            </p>
            <p className="text-xs text-gray-500 mb-6">
              {t('changePassword.successNote')}
            </p>
            <Button
              variant="primary"
              onClick={() => {
                const tenantUrlName = tenant?.tenanturlname || tenanturlname
                navigate(`/${tenantUrlName}/signin`)
              }}
            >
              {t('changePassword.signInButton')}
            </Button>
          </div>
        )}
        {/* Change Password - END */}
      </div>
    </>
  )
}

const ChangePassword = () => {
  const { tenant } = useTenant()
  const [recaptchaSiteKey, setRecaptchaSiteKey] = React.useState(null)

  React.useEffect(() => {
    // Wait for tenant to load, then set the recaptcha key
    const key =
      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
      tenant?.tenantmasterconfig?.recaptcha?.siteKey
    setRecaptchaSiteKey(key)
  }, [tenant])

  // Don't render until we have the key
  if (!recaptchaSiteKey) {
    return null
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      language="en"
      scriptProps={{ async: true, defer: true, appendTo: 'head' }}
      // container={{
      //   element: 'captcha-inline',
      //   parameters: {
      //     badge: 'inline',
      //   },
      // }}
    >
      <ChangePasswordForm />
    </GoogleReCaptchaProvider>
  )
}

export default ChangePassword
